import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { TicketPicker, TicketCta, TermsNote, doorOnlyFor } from "@/components/tickets/TicketPicker";
import { ResultBanner } from "@/components/tickets/TicketUI";
import { createTicketOrder, errorMessage, fetchTicketAvailability } from "@/lib/api";
import { fmtKr, isEmail, isPhone, needsEmail, needsPhone, smsFeeFor } from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * Ticket module for one event.
 * Desktop (lg+): a sticky panel in the right column. Mobile: a sticky bottom bar that opens a Drawer.
 * Both share the same state so switching viewport never loses the selection.
 */
export const TicketPanel = ({ eventId, className, mobileBar = true }) => {
  const navigate = useNavigate();
  const { data: av, isLoading, isError, refetch } = useQuery({ queryKey: ["tickets", eventId], queryFn: () => fetchTicketAvailability(eventId), refetchInterval: 60000 });
  const [qty, setQty] = useState({});
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", notify: "email", sms_fee_accepted: false });
  const [touched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);

  const ticketTotal = useMemo(() => (av?.types || []).reduce((s, t) => s + (qty[t.id] || 0) * t.price, 0), [av, qty]);
  const count = useMemo(() => Object.values(qty).reduce((s, n) => s + n, 0), [qty]);
  // SMS / both exist only while the house has SMS on – otherwise the choice is silently e-mail, as before
  const notify = av?.sms_available ? buyer.notify : "email";
  // One flat surcharge per order for an SMS ticket (server decides; this mirrors it). Picking e-mail again removes it.
  const smsFee = smsFeeFor(av, notify);
  const total = ticketTotal + smsFee;
  const feeOk = smsFee === 0 || !!buyer.sms_fee_accepted;

  const order = useMutation({
    mutationFn: () => createTicketOrder(eventId, { name: buyer.name.trim(), email: buyer.email.trim(), phone: buyer.phone.trim(), notify, sms_fee_accepted: smsFee > 0 && !!buyer.sms_fee_accepted, lines: Object.entries(qty).filter(([, n]) => n > 0).map(([type_id, n]) => ({ type_id, qty: n })) }),
    onSuccess: (o) => { setOpen(false); toast.success(o.status === "waitlist" ? "Du står på ventelista" : o.status === "confirmed" ? (o.mode === "free" ? "Du er påmeld" : "Billetten er klar") : "Plassane er haldne mellombels – betal med Vipps for å fullføre"); navigate(`/billett/${o.token}`); },
    onError: (e) => { toast.error(errorMessage(e)); refetch(); },
  });

  if (isLoading) return <div className={cn("space-y-3", className)} data-testid="ticket-panel-loading"><Skeleton className="h-5 w-24" /><Skeleton className="h-16 rounded-[12px]" /><Skeleton className="h-14 rounded-[12px]" /></div>;
  if (isError) return <ResultBanner variant="warn" title="Fekk ikkje lasta billettane" text="Sjekk nettet og prøv igjen." action={<Button variant="outline" className="bg-transparent" onClick={() => refetch()} data-testid="ticket-panel-retry">Prøv igjen</Button>} className={className} testId="ticket-panel-error" />;
  if (!av?.enabled) return null;

  const closed = !av.sales_open;
  const waitlistMode = av.sold_out && av.waitlist_enabled;
  const doorOnly = doorOnlyFor(av, closed);
  const doorInfoOnly = doorOnly && (closed || !waitlistMode);
  const emailOk = needsEmail(notify) ? isEmail(buyer.email) : !buyer.email.trim() || isEmail(buyer.email);
  const phoneOk = needsPhone(notify) ? isPhone(buyer.phone) : true;
  const valid = count > 0 && buyer.name.trim().length >= 2 && emailOk && phoneOk && feeOk;
  const missing = [buyer.name.trim().length < 2 && "namn", needsEmail(notify) && !isEmail(buyer.email) && "e-post", !emailOk && !needsEmail(notify) && "ei gyldig e-postadresse (eller tomt felt)", needsPhone(notify) && !isPhone(buyer.phone) && "mobilnummer", !feeOk && `avkryssinga for SMS-tillegget (${fmtKr(smsFee)}) – eller vel e-post, som er gratis`].filter(Boolean);
  const label = av.free ? "Påmelding" : "Billettar";
  const title = doorInfoOnly ? "Billettar i døra" : closed ? "Salet er stengt" : waitlistMode ? "Venteliste" : av.free ? "Meld deg på" : "Vel billettar";

  const submit = (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (count === 0) { toast.error(waitlistMode ? "Vel kor mange plassar du vil stå på venteliste for" : "Vel minst éin billett først"); return; }
    if (!valid) { toast.error(`Fyll inn ${missing.length > 1 ? `${missing.slice(0, -1).join(", ")} og ${missing[missing.length - 1]}` : missing[0]}`); return; }
    order.mutate();
  };

  const availabilityChip = doorOnly ? "Billettar i døra" : closed ? "Stengt" : av.sold_out ? (av.waitlist_enabled ? "Utseld · venteliste" : "Utseld") : av.unlimited ? "Ope" : av.show_numbers === false ? (av.few_left ? "Få att" : "Ledige plassar") : av.few_left ? `Få att – ${av.available}` : `${av.available} ledige`;
  const chipTone = closed || av.sold_out ? "text-muted-foreground" : av.few_left ? "text-sol" : "text-muted-foreground";
  const pickerProps = { av, qty, setQty, buyer, setBuyer, notify, touched, count, total, ticketTotal, smsFee, waitlistMode, closed };
  const ctaProps = { av, count, total, smsFee, pending: order.isPending, waitlistMode, closed };
  const fromPrice = av.free ? "Gratis" : `Frå ${fmtKr(Math.min(...av.types.map((t) => t.price)))}`;

  return (
    <>
      {/* Desktop: sticky panel */}
      <section id="billettar" className={cn("hidden scroll-mt-28 lg:block lg:sticky lg:top-24", className)} data-testid="ticket-panel" aria-labelledby="ticket-panel-title">
        <form onSubmit={submit} noValidate className="rounded-[20px] border border-border bg-card p-6 shadow-soft">
          <div className="flex items-baseline justify-between gap-4">
            <p className="t-mono text-muted-foreground">{label}</p>
            <p className={cn("flex items-center gap-1.5 text-[13px] font-medium tabular", chipTone)} data-testid="ticket-availability"><span className={cn("h-1.5 w-1.5 rounded-full", av.few_left && !av.sold_out ? "bg-sol" : "bg-current opacity-60")} aria-hidden="true" />{availabilityChip}</p>
          </div>
          <h2 id="ticket-panel-title" className="t-h3 mt-2">{title}</h2>
          <TicketPicker {...pickerProps} className="mt-5" />
          <TicketCta {...ctaProps} className="mt-5" />
          {!closed && <TermsNote terms={av.terms} className="mt-4" />}
        </form>
      </section>

      {/* Mobile: sticky bar + drawer */}
      <div className="lg:hidden">
        {mobileBar && <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-paper/90 backdrop-blur-md safe-bottom" data-testid="ticket-bar">
          <div className="mx-auto flex max-w-site items-center justify-between gap-4 px-5 pt-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[15px] font-semibold tabular">{fromPrice}</div>
              <div className={cn("mt-0.5 flex items-center gap-1.5 text-[13px] tabular", chipTone)} data-testid="ticket-bar-availability"><span className={cn("h-1.5 w-1.5 rounded-full", av.few_left && !av.sold_out ? "bg-sol" : "bg-current opacity-60")} aria-hidden="true" />{availabilityChip}</div>
            </div>
            <Button type="button" size="lg" className="h-12 shrink-0 rounded-[12px] px-5 text-[15px]" onClick={() => setOpen(true)} data-testid="ticket-panel-open-drawer-button">
              <Ticket className="h-4 w-4" /> {doorInfoOnly || closed ? "Sjå info" : waitlistMode ? "Venteliste" : av.free ? "Meld deg på" : "Vel billettar"}
            </Button>
          </div>
        </div>}

        <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
          <DrawerContent className="max-h-[92dvh] rounded-t-[24px] border-border bg-background" data-testid="ticket-drawer">
            <div className="flex items-start justify-between gap-4 px-5 pb-2 pt-1">
              <div className="min-w-0">
                <p className="t-mono text-muted-foreground">{label}</p>
                <DrawerTitle className="t-h3 mt-1">{title}</DrawerTitle>
                <DrawerDescription className="sr-only">Vel tal billettar og fyll inn kontaktinformasjon.</DrawerDescription>
              </div>
              <DrawerClose asChild><Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-full" aria-label="Lukk" data-testid="ticket-drawer-close"><X className="h-5 w-5" /></Button></DrawerClose>
            </div>
            <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
                <TicketPicker {...pickerProps} compact />
                {!closed && <TermsNote terms={av.terms} className="mt-4" />}
              </div>
              <div className="border-t border-border bg-background px-5 pt-3 safe-bottom">
                <TicketCta {...ctaProps} />
              </div>
            </form>
          </DrawerContent>
        </Drawer>
      </div>
    </>
  );
};
